/**
 * Shared Server-Sent-Events client — the single source for parsing a streamed
 * `fetch` Response body into JSON events.
 *
 * We read the body by hand (not `EventSource`) because every Tray SSE turn is a
 * POST that must carry an auth token in the request body/headers — `EventSource`
 * can only GET and can't set headers. Five callers used to inline this exact
 * reader loop + frame parser (`askChat`, `portalVoice`, the two AI debuggers, the
 * team-run console); they now share `parseSseFrames` + `consumeSse`.
 */

/**
 * Parse the complete `\n\n`-delimited SSE frames out of an accumulating buffer.
 * Pure + chunk-boundary-safe: a frame split across two network reads stays in the
 * returned `buffer` until its terminator arrives. Each frame's `data:` line is
 * JSON-parsed; a malformed payload is skipped (never throws into the stream).
 */
export function parseSseFrames<T>(
  buffer: string,
  chunk: string,
): { events: T[]; buffer: string } {
  let buf = buffer + chunk;
  const events: T[] = [];
  let sep: number;
  while ((sep = buf.indexOf("\n\n")) !== -1) {
    const frame = buf.slice(0, sep);
    buf = buf.slice(sep + 2);
    for (const line of frame.split("\n")) {
      if (!line.startsWith("data:")) continue;
      try {
        events.push(JSON.parse(line.slice(5).trim()) as T);
      } catch {
        /* ignore a malformed frame */
      }
    }
  }
  return { events, buffer: buf };
}

/**
 * Read a fetch Response body as an SSE event stream, invoking `onEvent` for each
 * parsed `data:` JSON payload until the stream closes. The one reader loop shared
 * by every SSE consumer — resolves when the stream ends.
 */
export async function consumeSse<T>(
  body: ReadableStream<Uint8Array>,
  onEvent: (ev: T) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const decoded = parseSseFrames<T>(buffer, decoder.decode(value, { stream: true }));
    buffer = decoded.buffer;
    for (const ev of decoded.events) onEvent(ev);
  }
}
